import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useLang } from "@/context/LanguageContext";
import { Truck } from "lucide-react";

// HAG Director/Coordinator: read-only trade log of the MG↔RIC yarn flow.
// Includes agreed_rate visibility (rate visible to director per spec).
export default function RICTradeLog() {
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [pos, setPos] = useState([]);
  const [disc, setDisc] = useState([]);

  useEffect(() => {
    (async () => {
      setPos(await ric.tradeLog());
      setDisc(await ric.discrepancies());
    })();
  }, []);

  const totalKg = pos.reduce((a, p) => a + p.lines.reduce((x, ln) => x + ln.kg_requested, 0), 0);
  const openDisc = disc.filter((d) => d.status === "Open").length;

  return (
    <div data-testid="ric-tradelog">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
            <Truck size={22} /> {t("MG ↔ RIC yarn trade log", "MG ↔ RIC यार्न ट्रेड लॉग")}
          </h1>
          <p className="text-xs text-[#7C8E9E]">{t("Read-only oversight of the MG Rugs to Royal Indian Carpets yarn trade.",
            "MG Rugs से Royal Indian Carpets के बीच का यार्न ट्रेड — केवल पढ़ें।")}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="hag-surface p-3"><div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">POs</div><div className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>{pos.length}</div></div>
        <div className="hag-surface p-3"><div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">Total kg</div><div className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>{totalKg.toFixed(1)}</div></div>
        <div className="hag-surface p-3"><div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">Open discrepancies</div><div className="text-2xl font-bold" style={{ color: openDisc > 0 ? "#b91c1c" : "var(--brand-navy)" }}>{openDisc}</div></div>
      </div>

      <div className="hag-surface overflow-x-auto" data-testid="ric-tradelog-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E] bg-[#FAF6F0]">
              <th className="text-left px-3 py-2">PO#</th>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Lines</th>
              <th className="text-right px-3 py-2">Req kg</th>
              <th className="text-right px-3 py-2">Recv kg</th>
              <th className="text-right px-3 py-2">Trade value ₹</th>
            </tr>
          </thead>
          <tbody>
            {pos.map((p) => {
              const reqKg = p.lines.reduce((a, ln) => a + ln.kg_requested, 0);
              const recvKg = p.lines.reduce((a, ln) => a + (ln.kg_received || 0), 0);
              const tradeValue = p.lines.reduce((a, ln) => a + (ln.kg_received || 0) * (ln.agreed_rate || 0), 0);
              return (
                <tr key={p.id} className="border-t border-[#1B3A5C]/10" data-testid={`ric-tradelog-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{p.po_number}</td>
                  <td className="px-3 py-2 text-xs">{new Date(p.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2"><span className="status-chip pace-none">{p.status}</span></td>
                  <td className="px-3 py-2 text-right">{p.lines.length}</td>
                  <td className="px-3 py-2 text-right">{reqKg.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right">{recvKg.toFixed(1)}</td>
                  <td className="px-3 py-2 text-right">{tradeValue > 0 ? `₹${tradeValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : "—"}</td>
                </tr>
              );
            })}
            {pos.length === 0 && <tr><td colSpan={7} className="text-center text-xs py-6 text-[#7C8E9E]">No trade yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
